import * as microsoftTeams from '@microsoft/teams-js';
import { Context, OpenConversationRequest } from '@microsoft/teams-js';

export interface IAppWindow {
  // tslint:disable-next-line: no-any ~ postMessage can take anything as the message.
  postMessage(message: any): void;
  addEventListener(type: string, listener: Function): void;
}

export type ITaskInfo = microsoftTeams.TaskInfo;

export enum CommonTaskModuleErrors {
  UserCancelled = 'User cancelled/closed the task module.'
}

export interface ITasks {
  startTask: (
    taskInfo: ITaskInfo,
    submitHandler?: (err: string, result: string) => void
  ) => IAppWindow;
  updateTask: (taskInfo: ITaskInfo) => void;
  submitTask: (result?: string | object, appIds?: string | string[]) => void;
}

export type IOpenConversationRequest = OpenConversationRequest;

export interface IConversations {
  openConversation: (openConversationRequest: IOpenConversationRequest) => void;
  closeConversation: () => void;
}

export type IAuthTokenRequest = microsoftTeams.authentication.AuthTokenRequest;

export interface IAuthentication {
  getAuthToken: (
    authTokenRequest: microsoftTeams.authentication.AuthTokenRequest
  ) => void;
}

export type IInstanceSettings = microsoftTeams.settings.Settings;

export interface ISettings {
  registerOnSaveHandler: (
    handler: (evt: microsoftTeams.settings.SaveEvent) => void
  ) => void;
  registerOnRemoveHandler: (
    handler: (evt: microsoftTeams.settings.RemoveEvent) => void
  ) => void;
  setValidityState: (validityState: boolean) => void;
  setSettings: (
    instanceSettings: IInstanceSettings,
    onComplete?: (status: boolean, reason?: string) => void
  ) => void;
  getSettings: (
    callback: (instanceSettings: microsoftTeams.settings.Settings) => void
  ) => void;
}

/**
 * Indicates the various types of roles of a user in a team.
 */
export enum UserTeamRole {
  Admin = 0,
  User = 1,
  Guest = 2
}

/**
 * Indicates the team type, currently used to distinguish between different team
 * types in Office 365 for Education (team types 1, 2, 3, and 4).
 */
export enum TeamType {
  Standard = 0,
  Edu = 1,
  Class = 2,
  Plc = 3,
  Staff = 4
}

export enum HostClientType {
  android = 'android',
  desktop = 'desktop',
  ios = 'ios',
  rigel = 'rigel',
  web = 'web'
}

export enum ChannelType {
  Private = 'Private',
  Regular = 'Regular'
}

interface IOverridenProperties {
  userTeamRole?: UserTeamRole;
  teamType?: TeamType;
  hostClientType?: HostClientType;
  channelType?: ChannelType;
}

export type IContext = Omit<Context, keyof IOverridenProperties> &
  IOverridenProperties;

export interface ITeamsClientSDK {
  conversations: IConversations;
  tasks: ITasks;
  authentication: IAuthentication;
  settings: ISettings;
  // tslint:disable-next-line: no-any ~ hostWindow can be anything
  initialize: (hostWindow?: any) => void;
  getContext: (callback: (context: IContext) => void) => void;
  registerOnThemeChangeHandler: (handler: (theme: string) => void) => void;
}

export let teamsSdk: ITeamsClientSDK;

export const isFakeClientSdk = () => {
  return process.env.REACT_APP_CLIENT_SDK_MODE === 'fake-client-sdk';
};

export const setTeamsSdk = (): Promise<ITeamsClientSDK> => {
  if (isFakeClientSdk()) {
    return new Promise(resolve => {
      import('./teamsSdk.mock').then(teamsSdkMockModule => {
        teamsSdk = teamsSdkMockModule.fakeMicrosoftTeams;
        resolve(teamsSdkMockModule.fakeMicrosoftTeams);
      });
    });
  }

  return new Promise(resolve => {
    teamsSdk = microsoftTeams as typeof teamsSdk;
    resolve(teamsSdk);
  });
};
